// ---------------------------------------------------------------------------
// vocabulary.ts — typed read surface for the closed expression/event/action
// vocabularies (Studio Vocabulary panel).
//
// Migrated from cogo-studio's kg-client localGet{Prefixes,Accessors,Events,
// Actions} (the documented Phase 2a Step 4 Repository bypass): the SPARQL
// now lives HERE, behind typed Repository methods, instead of in the studio
// feature layer. Queries are verbatim ports — same shards, same predicates,
// same row shapes.
//
// The SPARQL strings and row mappers are exported separately so the row
// mapping is unit-testable without an Oxigraph instance (the wasm path is
// exercised by the studio-side integration tests, same convention as the
// other domain methods).
// ---------------------------------------------------------------------------

/** Canonical binding prefix (expression-prefix-v1.ttl, 9 instances). */
export interface ExpressionPrefixItem {
  iri: string
  name: string
  semantics: string
  storage: string
  readOnly: boolean
}

/** Expression accessor (expression-accessors-v1.ttl, 50 instances). */
export interface ExpressionAccessorItem {
  iri: string
  name: string
  category: string
  returnKind: string
}

/** UI event vocabulary entry (events-v1.ttl). */
export interface UiEventVocabularyItem {
  iri: string
  name: string
  domain: string
  description?: string
}

/** UI action vocabulary entry (actions-v1.ttl, metadata-carrying subset). */
export interface UiActionVocabularyItem {
  iri: string
  name: string
  category: string
  description?: string
}

const COGO_PREFIX = "PREFIX cogo: <https://cogo.xyz/ns/v1#>"

export const EXPRESSION_PREFIX_SPARQL = `
  ${COGO_PREFIX}
  SELECT ?iri ?name ?semantics ?storage ?readOnly
  WHERE {
    ?iri a cogo:ExpressionPrefix ;
         cogo:prefixName ?name .
    OPTIONAL { ?iri cogo:prefixSemantics ?semantics . }
    OPTIONAL { ?iri cogo:prefixStorage ?storage . }
    OPTIONAL { ?iri cogo:readOnly ?readOnly . }
  }
  ORDER BY ?name
`

export const EXPRESSION_ACCESSOR_SPARQL = `
  ${COGO_PREFIX}
  SELECT ?iri ?name ?category ?returnKind
  WHERE {
    ?iri a cogo:ExpressionAccessor ;
         cogo:accessorName ?name ;
         cogo:accessorCategory ?category ;
         cogo:accessorReturnKind ?returnKind .
  }
  ORDER BY ?category ?name
`

export const UI_EVENT_VOCABULARY_SPARQL = `
  ${COGO_PREFIX}
  SELECT ?iri ?name ?domain ?description
  WHERE {
    ?iri a cogo:Event .
    OPTIONAL { ?iri cogo:name ?name . }
    OPTIONAL { ?iri cogo:domain ?domain . }
    OPTIONAL { ?iri cogo:description ?description . }
  }
  ORDER BY ?domain ?name
`

/**
 * cogo:category is REQUIRED — it filters the cogo:Action instances down to
 * the metadata-carrying subset (platformSupport sub-nodes lack it and are
 * intentionally excluded).
 */
export const UI_ACTION_VOCABULARY_SPARQL = `
  ${COGO_PREFIX}
  SELECT ?iri ?name ?category ?description
  WHERE {
    ?iri a cogo:Action ;
         cogo:category ?category .
    OPTIONAL { ?iri cogo:name ?name . }
    OPTIONAL { ?iri cogo:description ?description . }
  }
  ORDER BY ?category ?name
`

type SparqlRow = Record<string, string>

function asString(value: unknown): string {
  return typeof value === "string" ? value : ""
}

function asBoolean(value: unknown): boolean {
  if (typeof value === "boolean") return value
  if (typeof value === "string") return value.toLowerCase() === "true"
  return false
}

/** Last path segment of an IRI (after the final '/', '#' or ':'). */
export function vocabularyIriTail(iri: string): string {
  const match = /[^/#:]+$/.exec(iri)
  return match ? match[0] : iri
}

export function mapExpressionPrefixRows(rows: SparqlRow[]): ExpressionPrefixItem[] {
  return rows.map((row) => ({
    iri: asString(row.iri),
    name: asString(row.name),
    semantics: asString(row.semantics),
    storage: asString(row.storage),
    readOnly: asBoolean(row.readOnly),
  }))
}

export function mapExpressionAccessorRows(rows: SparqlRow[]): ExpressionAccessorItem[] {
  return rows.map((row) => ({
    iri: asString(row.iri),
    name: asString(row.name),
    category: asString(row.category),
    returnKind: asString(row.returnKind),
  }))
}

export function mapUiEventVocabularyRows(rows: SparqlRow[]): UiEventVocabularyItem[] {
  return rows.map((row) => {
    const iri = asString(row.iri)
    const name =
      typeof row.name === "string" && row.name.length > 0 ? row.name : vocabularyIriTail(iri)
    const item: UiEventVocabularyItem = {
      iri,
      name,
      domain: asString(row.domain),
    }
    if (typeof row.description === "string" && row.description.length > 0) {
      item.description = row.description
    }
    return item
  })
}

export function mapUiActionVocabularyRows(rows: SparqlRow[]): UiActionVocabularyItem[] {
  return rows.map((row) => {
    const iri = asString(row.iri)
    const name =
      typeof row.name === "string" && row.name.length > 0 ? row.name : vocabularyIriTail(iri)
    const item: UiActionVocabularyItem = {
      iri,
      name,
      category: asString(row.category),
    }
    if (typeof row.description === "string" && row.description.length > 0) {
      item.description = row.description
    }
    return item
  })
}
